import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  ArrowBackIcon,
  Button,
  Grid,
  IconButton,
  InfoOutlineIcon,
  MenuItem,
  Skeleton,
  Stack,
  ToolTip,
  Typography,
  Menu,
  Box
} from '@esgian/esgianui';
import jsPDF from 'jspdf';
import {
  generatePortData,
  generateWaitingTime,
  generateActivityProfile,
  addNewPageIfNeeded,
  generateCargoGhgEmissionSummary,
  myCargoTocSummary,
  myCargoHocTable,
  overallVoyageTable,
  overallVoyageEmissionIntensityTable,
  costEstMyCargoTable,
  euEtsOverallVoyageTable,
  voyagesEstEtsCostTable,
  CO2EmissionTable,
  fuelConsumptionTable
} from '@helpers/pdfGeneratorServices';
import { generateGhcEmissionReport } from './GhcEmissionReportGenerator';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';
import VoyageGroupsPopup from '@components/Sections/VoyageAnalytics/VoyageGroupSection/VoyageGroupsPopup';
import reportIcon from '@assets/images/reportIcon.png';
import reportIconBlack from '@assets/images/reportIconBlack.png';
import esgianLogo from '@assets/images/esgianLogo.png';
import { useSegment } from '@hooks';
import { useSelector } from 'react-redux';
import { getThemeMode } from '@store/features';

export const sectionShortNames = {
  1: 'Voyage Summary',
  2: 'Emission and ETS',
  3: 'Vessel Specification'
};

// AIS Data Constants
export const AisDataStatusColor = {
  'Very Good': '#80DFEB',
  Good: '#4CAF50',
  Medium: 'white',
  Low: '#FF9800',
  'Very Low': '#EF5350'
};

export const AisDataDescription = {
  'Very Good': 'AIS Data quality is excellent',
  Good: 'AIS Data quality is upto requirements',
  Medium:
    'Some AIS data points corresponding to this voyage have not been received from the vessel, therefore corrections have been done on modelled emissions accordingly.',
  Low: 'AIS data corresponding to this voyage received from the vessel seems to be lower than usual, therefore corrections have been done on modelled emissions',
  'Very Low':
    'We have received very less AIS data for this voyage from the vessel. Correction factors have been applied to emissions to account for missing data. The estimations are prone to have errors due to lack of data.'
};

export const getAisStatus = (aisGrade) => {
  let status = 'Very Low';

  if (aisGrade > 95) {
    status = 'Very Good';
  } else if (aisGrade > 85 && aisGrade <= 95) {
    status = 'Good';
  } else if (aisGrade > 80 && aisGrade <= 85) {
    status = 'Medium';
  } else if (aisGrade > 70 && aisGrade <= 80) {
    status = 'Low';
  }

  return status;
};

const CompletedVoyageProfileHeader = ({
  voyageData,
  voyageOverview,
  sectionShortNames,
  section,
  loadedData
}) => {
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState(null);

  const themeMode = useSelector(getThemeMode);

  const pdf = new jsPDF('landscape');
  const pdfHeader = pdf.addImage(esgianLogo, 'PNG', 230, 5, 56.5, 16);
  const { isRoRo } = useSegment();

  // Calculate AIS status
  const { aisStatus, aisColor, aisDataDescription } = useMemo(() => {
    if (!voyageData) return { aisStatus: '', aisColor: '', aisDataDescription: '' };

    const { voyageTransitDetails } = voyageData;
    const aisDensity = voyageTransitDetails?.aisDensity ?? 0;
    const status = getAisStatus(aisDensity);

    return {
      aisStatus: status,
      aisColor: AisDataStatusColor[status],
      aisDataDescription: AisDataDescription[status]
    };
  }, [voyageData]);

  const { voyagePortCalls, voyageTransitDetails } = voyageData ?? {};

  // Menu handlers
  const handleButtonClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const voyageOverviewReport = async () => {
    let yOffset = 5;
    pdfHeader;
    yOffset += 25;

    yOffset = generatePortData(pdf, voyagePortCalls.portCalls, yOffset, addNewPageIfNeeded);
    yOffset = generateActivityProfile(pdf, voyageTransitDetails, yOffset, addNewPageIfNeeded);
    yOffset = generateWaitingTime(pdf, voyagePortCalls, yOffset, addNewPageIfNeeded);

    pdf.save('Voyage-summary-report.pdf');
  };

  const ghgEmissionReport = async () => {
    generateGhcEmissionReport(loadedData, voyagePortCalls, isRoRo, voyageOverview);
  };
  const euEtsCostEstimation = async () => {
    let yOffset = 5;
    pdfHeader;
    yOffset += 25;

    if (loadedData) {
      yOffset = costEstMyCargoTable(pdf, loadedData, yOffset, addNewPageIfNeeded);
      yOffset = euEtsOverallVoyageTable(pdf, loadedData, yOffset, addNewPageIfNeeded);
      yOffset += 40;
      yOffset = voyagesEstEtsCostTable(pdf, loadedData, yOffset, addNewPageIfNeeded);
    }

    pdf.save('EU ETS Cost Estimation.pdf');
  };

  const overallVoyageReport = async () => {
    let yOffset = 5;
    pdfHeader;
    yOffset += 25;

    yOffset = generatePortData(pdf, voyagePortCalls.portCalls, yOffset, addNewPageIfNeeded);
    yOffset = generateActivityProfile(pdf, voyageTransitDetails, yOffset, addNewPageIfNeeded);
    yOffset = generateWaitingTime(pdf, voyagePortCalls, yOffset, addNewPageIfNeeded);
    if (loadedData) {
      yOffset = generateCargoGhgEmissionSummary(pdf, loadedData, yOffset, addNewPageIfNeeded);
      yOffset = myCargoTocSummary(pdf, loadedData, yOffset, addNewPageIfNeeded);
      yOffset = myCargoHocTable(pdf, loadedData, yOffset, addNewPageIfNeeded);
      yOffset = overallVoyageTable(pdf, loadedData, yOffset, addNewPageIfNeeded);
      yOffset = overallVoyageEmissionIntensityTable(pdf, loadedData, yOffset, addNewPageIfNeeded);
      yOffset = costEstMyCargoTable(pdf, loadedData, yOffset, addNewPageIfNeeded);
      yOffset = euEtsOverallVoyageTable(pdf, loadedData, yOffset, addNewPageIfNeeded);
      yOffset = voyagesEstEtsCostTable(pdf, loadedData, yOffset, addNewPageIfNeeded);
      yOffset = CO2EmissionTable(pdf, loadedData, yOffset, addNewPageIfNeeded);
      yOffset = fuelConsumptionTable(pdf, loadedData, yOffset, addNewPageIfNeeded);
    }

    pdf.save('Overall-voyage-report.pdf');
  };

  const handleReportOptionClick = (reportType) => {
    switch (reportType) {
      case 1:
        voyageOverviewReport();
        break;
      case 2:
        ghgEmissionReport();
        break;
      case 3:
        euEtsCostEstimation();
        break;
      case 4:
        overallVoyageReport();
        break;
      default:
        break;
    }
    handleClose();
  };

  return (
    <Grid item xs={12}>
      <Stack direction={'row'} justifyContent={'space-between'}>
        <Stack direction={'row'} spacing={2}>
          <IconButton
            onClick={() => navigate(-1)}
            sx={{
              background: '#80DFEB33',
              borderRadius: '0.5rem',
              height: 'fit-content'
            }}>
            <ArrowBackIcon fontSize={'small'} />
          </IconButton>
          <Stack spacing={1}>
            <Typography variant={'h5'}>
              {!voyageData ? (
                <Skeleton width={'20vw'} height={'1em'} variant={'rectangular'} />
              ) : (
                `Voyage from ${voyageOverview.departurePort} to ${voyageOverview.arrivalPort}`
              )}
            </Typography>
            <Typography id={sectionShortNames?.[section]}>
              {!voyageData ? (
                <Skeleton width={'20vw'} />
              ) : (
                `From ${moment(voyageOverview.departureTime).format('YYYY-MM-DD')} to ${moment(
                  voyageOverview.arrivalTime
                ).format('YYYY-MM-DD')}`
              )}
            </Typography>
          </Stack>
        </Stack>
        <Stack direction={'row'} spacing={2}>
          <Button
            variant="text"
            sx={{
              height: 'fit-content',
              color: 'text.primary',
              textTransform: 'none'
            }}
            startIcon={
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="8"
                height="8"
                viewBox="0 0 8 8"
                fill="none">
                <circle cx="4" cy="4" r="4" fill={aisColor} />
              </svg>
            }
            endIcon={
              <ToolTip title={aisDataDescription}>
                <InfoOutlineIcon sx={{ fontSize: 16 }} />
              </ToolTip>
            }>
            <Typography variant="body2">{`AIS: ${aisStatus}`}</Typography>
          </Button>
          <VoyageGroupsPopup mode="profile" />

          <Box>
            <Button
              variant="text"
              color={'primary'}
              id={'ais-status'}
              sx={{
                height: 'fit-content',
                size: '0.875rem',
                textTransform: 'none'
              }}
              onClick={handleButtonClick}
              startIcon={
                themeMode ? (
                  <img src={reportIcon} alt={'reportIcon'} />
                ) : (
                  <img src={reportIconBlack} alt={'reportIcon'} />
                )
              }>
              <Typography variant="body2">REPORT</Typography>
            </Button>
            <Menu
              id="va-report-download-menu"
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleClose}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'left'
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'left'
              }}>
              <MenuItem onClick={() => handleReportOptionClick(1)}>Voyage Summary</MenuItem>
              {loadedData && (
                <>
                  <MenuItem onClick={() => handleReportOptionClick(2)}>
                    GHG Emission for scope 3 emission reporting
                  </MenuItem>
                  <MenuItem onClick={() => handleReportOptionClick(3)}>
                    EU ETS Cost Estimation
                  </MenuItem>
                </>
              )}
              <MenuItem onClick={() => handleReportOptionClick(4)}>Overall Voyage Report</MenuItem>
            </Menu>
          </Box>
        </Stack>
      </Stack>
    </Grid>
  );
};

CompletedVoyageProfileHeader.propTypes = {
  voyageData: PropTypes.object,
  voyageOverview: PropTypes.object,
  sectionShortNames: PropTypes.object,
  section: PropTypes.number,
  loadedData: PropTypes.object,
  themeMode: PropTypes.bool,
  onReportGenerate: PropTypes.func
};

export default CompletedVoyageProfileHeader;
